import type { PlaneId } from './types';
import type { Annotation, BBox } from './scanStore';

/**
 * Full COCO (Common Objects in Context) object-detection format.
 *
 * The viewer works in an ergonomic flat model ({@link Annotation}: one box in
 * [0,1] frac coords per plane+slice), but everything it PERSISTS is a
 * spec-compliant COCO dataset — pixel-space `bbox`, `area`, a rectangle
 * `segmentation` polygon, `iscrowd`, and proper `image_id`/`category_id`
 * references into `images[]` / `categories[]`. `toCoco` / `fromCoco` convert
 * between the two; the CBCT frame each image maps back to is carried on the
 * image entry (`plane`/`slice`, also encoded in `file_name`).
 *
 * Spec: https://cocodataset.org/#format-data
 */

export interface CocoInfo {
    description: string;
    version: string;
    year: number;
    contributor: string;
    date_created: string;
}

export interface CocoLicense {
    id: number;
    name: string;
    url: string;
}

/** One annotatable frame. `plane`/`slice` (a CBCT extension) locate it in the
 *  volume; they are also encoded in `file_name` for standalone COCO tooling. */
export interface CocoImage {
    id: number;
    width: number;
    height: number;
    file_name: string;
    license: number;
    date_captured: string;
    plane: PlaneId;
    slice: number;
}

export interface CocoAnnotation {
    id: number;
    image_id: number;
    category_id: number;
    /** [x, y, width, height] in absolute pixels, top-left origin. */
    bbox: BBox;
    /** Area of the box in pixels² (== bbox area for a rectangle). */
    area: number;
    /** Polygon rings as flat [x0,y0,x1,y1,…]; a rectangle is a single 4-point ring. */
    segmentation: number[][];
    iscrowd: 0 | 1;
    /** CVAT-style extension bag for viewer-only state (kept out of the core fields). */
    attributes?: { hidden?: boolean };
}

export interface CocoCategory {
    id: number;
    name: string;
    supercategory: string;
}

export interface CocoDataset {
    info: CocoInfo;
    licenses: CocoLicense[];
    images: CocoImage[];
    annotations: CocoAnnotation[];
    categories: CocoCategory[];
}

/** Per-plane pixel dimensions, needed to map frac coords ↔ COCO pixel coords. */
export type PlaneDims = Record<PlaneId, { frame_w: number; frame_h: number }>;

const SUPERCATEGORY = 'cbct';
// COCO requires every annotation to reference a valid category, so boxes with no
// label yet are grouped under this reserved category (mapped back to '' on read).
const UNLABELED = 'unlabeled';
const PLANE_ORDER: Record<PlaneId, number> = { axial: 0, coronal: 1, sagittal: 2 };

const INFO: CocoInfo = {
    description: 'Odo CBCT annotations',
    version: '1.0',
    year: 0,
    contributor: 'Odo',
    date_created: ''
};

// Fixed, non-time-derived metadata keeps the serialized file stable across saves.
const round2 = (n: number) => Math.round(n * 100) / 100;
const isPlaneId = (v: unknown): v is PlaneId =>
    v === 'axial' || v === 'coronal' || v === 'sagittal';
const isBBox = (v: unknown): v is BBox =>
    Array.isArray(v) && v.length === 4 && v.every((n) => typeof n === 'number');
const frameKey = (plane: PlaneId, slice: number): string => `${plane}:${slice}`;
const fileName = (plane: PlaneId, slice: number): string =>
    `${plane}_${String(slice).padStart(5, '0')}.png`;
const catName = (label: string): string => label.trim() || UNLABELED;

export function emptyCoco(): CocoDataset {
    return { info: INFO, licenses: [], images: [], annotations: [], categories: [] };
}

export const isCocoEmpty = (d: CocoDataset): boolean => d.annotations.length === 0;

/**
 * Serialize the flat working model into a spec-compliant COCO dataset. Needs
 * `dims` to size each image and convert the frac bboxes into absolute pixels.
 * Category ids are assigned by sorted label name and image ids by plane→slice
 * order so the output is deterministic (no churn between identical saves).
 */
export function toCoco(annos: Annotation[], dims: PlaneDims): CocoDataset {
    const names = Array.from(new Set(annos.map((a) => catName(a.label)))).sort();
    const categories: CocoCategory[] = names.map((name, i) => ({
        id: i + 1,
        name,
        supercategory: SUPERCATEGORY
    }));
    const catId = new Map(categories.map((c) => [c.name, c.id]));

    // One image per distinct plane+slice, ordered plane→slice, ids assigned after sorting.
    const seen = new Map<string, CocoImage>();
    for (const a of annos) {
        const key = frameKey(a.plane, a.slice);
        if (seen.has(key)) continue;
        const { frame_w, frame_h } = dims[a.plane];
        seen.set(key, {
            id: 0,
            width: frame_w,
            height: frame_h,
            file_name: fileName(a.plane, a.slice),
            license: 0,
            date_captured: '',
            plane: a.plane,
            slice: a.slice
        });
    }
    const images = Array.from(seen.values()).sort((p, q) =>
        p.plane === q.plane ? p.slice - q.slice : PLANE_ORDER[p.plane] - PLANE_ORDER[q.plane]
    );
    const imageId = new Map<string, number>();
    images.forEach((img, i) => {
        img.id = i + 1;
        imageId.set(frameKey(img.plane, img.slice), img.id);
    });

    const annotations: CocoAnnotation[] = annos.map((a) => {
        const { frame_w, frame_h } = dims[a.plane];
        const x = round2(a.bbox[0] * frame_w);
        const y = round2(a.bbox[1] * frame_h);
        const w = round2(a.bbox[2] * frame_w);
        const h = round2(a.bbox[3] * frame_h);
        return {
            id: a.id,
            image_id: imageId.get(frameKey(a.plane, a.slice))!,
            category_id: catId.get(catName(a.label))!,
            bbox: [x, y, w, h] as BBox,
            area: round2(w * h),
            segmentation: [[x, y, x + w, y, x + w, y + h, x, y + h]],
            iscrowd: 0,
            ...(a.hidden ? { attributes: { hidden: true } } : {})
        };
    });

    return { info: INFO, licenses: [], images, annotations, categories };
}

/**
 * Rebuild the flat working model from a COCO dataset. Self-contained: pixel→frac
 * conversion uses each image's own `width`/`height`, so no external dims needed.
 */
export function fromCoco(ds: CocoDataset): Annotation[] {
    const nameById = new Map(ds.categories.map((c) => [c.id, c.name]));
    const imageById = new Map(ds.images.map((im) => [im.id, im]));
    const out: Annotation[] = [];
    for (const a of ds.annotations) {
        const im = imageById.get(a.image_id);
        if (!im) continue; // referential integrity is enforced on parse, but guard anyway
        const w = im.width || 1;
        const h = im.height || 1;
        const name = nameById.get(a.category_id) ?? UNLABELED;
        out.push({
            id: a.id,
            plane: im.plane,
            slice: im.slice,
            bbox: [a.bbox[0] / w, a.bbox[1] / h, a.bbox[2] / w, a.bbox[3] / h],
            label: name === UNLABELED ? '' : name,
            ...(a.attributes?.hidden ? { hidden: true } : {})
        });
    }
    return out;
}

// --- Validation -----------------------------------------------------------
// Lenient parsers: drop malformed entries rather than throwing, and drop any
// annotation whose image_id / category_id doesn't resolve, so a hydrated dataset
// is always referentially sound. A value that isn't a COCO object at all (e.g.
// data from a pre-COCO build) yields an empty dataset — those aren't migrated.

const parseImage = (v: unknown): CocoImage | null => {
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    if (typeof o.id !== 'number' || typeof o.width !== 'number' || typeof o.height !== 'number')
        return null;
    if (!isPlaneId(o.plane) || typeof o.slice !== 'number') return null;
    return {
        id: o.id,
        width: o.width,
        height: o.height,
        file_name: typeof o.file_name === 'string' ? o.file_name : fileName(o.plane, o.slice),
        license: typeof o.license === 'number' ? o.license : 0,
        date_captured: typeof o.date_captured === 'string' ? o.date_captured : '',
        plane: o.plane,
        slice: o.slice
    };
};

const parseCategory = (v: unknown): CocoCategory | null => {
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    if (typeof o.id !== 'number' || typeof o.name !== 'string') return null;
    return {
        id: o.id,
        name: o.name,
        supercategory: typeof o.supercategory === 'string' ? o.supercategory : SUPERCATEGORY
    };
};

const parseAnnotation = (v: unknown): CocoAnnotation | null => {
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    if (
        typeof o.id !== 'number' ||
        typeof o.image_id !== 'number' ||
        typeof o.category_id !== 'number'
    )
        return null;
    if (!isBBox(o.bbox)) return null;
    const [x, y, w, h] = o.bbox;
    const hidden = !!(
        o.attributes &&
        typeof o.attributes === 'object' &&
        (o.attributes as Record<string, unknown>).hidden === true
    );
    return {
        id: o.id,
        image_id: o.image_id,
        category_id: o.category_id,
        bbox: o.bbox,
        area: typeof o.area === 'number' ? o.area : w * h,
        segmentation:
            Array.isArray(o.segmentation) &&
            o.segmentation.every((s) => Array.isArray(s) && s.every((n) => typeof n === 'number'))
                ? (o.segmentation as number[][])
                : [[x, y, x + w, y, x + w, y + h, x, y + h]],
        iscrowd: o.iscrowd === 1 ? 1 : 0,
        ...(hidden ? { attributes: { hidden: true } } : {})
    };
};

export function parseCocoDataset(v: unknown): CocoDataset {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return emptyCoco();
    const d = v as Record<string, unknown>;
    const images = Array.isArray(d.images)
        ? d.images.map(parseImage).filter((x): x is CocoImage => x !== null)
        : [];
    const categories = Array.isArray(d.categories)
        ? d.categories.map(parseCategory).filter((x): x is CocoCategory => x !== null)
        : [];
    const imageIds = new Set(images.map((i) => i.id));
    const categoryIds = new Set(categories.map((c) => c.id));
    const annotations = Array.isArray(d.annotations)
        ? d.annotations
              .map(parseAnnotation)
              .filter(
                  (x): x is CocoAnnotation =>
                      x !== null && imageIds.has(x.image_id) && categoryIds.has(x.category_id)
              )
        : [];
    return { info: INFO, licenses: [], images, annotations, categories };
}
